import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import {
  ActionsDropdown,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { TextPill } from 'components/FbUI/TextPill'
import { FbModal } from 'components/UI/Modal'
import { useAuth } from 'context/authentication/useAuth'
import { InviteUserModal } from 'features/users/InviteUserModal'
import { User } from 'models/user'
import Select from 'components/FormUtils/Select'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { featureFlagService } from 'utils/featureFlagService'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import * as S from './styles'

const USER_TYPE_ADMIN = 'Admin'

export function UserTable() {
  const api = apiService()
  const { user } = useAuth()
  const { enableUserTypes } = featureFlagService()
  const queryClient = useQueryClient()

  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { control, reset, watch } = useForm<{ user_type: string }>({
    defaultValues: { user_type: '' },
  })

  const selectedType = watch('user_type')

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      return await api.getUsers()
    },
  })

  const { data: userTypes } = useQuery({
    queryKey: ['user-types'],
    queryFn: async () => {
      return await api.getUserTypes()
    },
    enabled: enableUserTypes && user?.user_type === USER_TYPE_ADMIN,
  })

  const isCurrentUserAdmin = user?.user_type === USER_TYPE_ADMIN

  const handleEdit = useCallback(
    (targetUser: User) => {
      setSelectedUser(targetUser)
      reset({ user_type: targetUser.user_type ?? 'Standard' })
    },
    [reset]
  )

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !selectedType) return
    setIsSubmitting(true)
    try {
      await api.updateUserType(selectedUser.id, selectedType)
      toast.success('User type updated successfully')
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['company-details'] })
      setSelectedUser(null)
    } catch {
      toast.error('Failed to update user type')
    } finally {
      setIsSubmitting(false)
    }
  }, [selectedUser, selectedType, api, refetch, queryClient])

  const columns = useMemo<ColumnDef<User, any>[]>(() => {
    const cols: ColumnDef<User, any>[] = [
      {
        accessorKey: 'first_name',
        header: 'First Name',
        size: 200,
      },
      {
        accessorKey: 'last_name',
        header: 'Last Name',
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 150,
        cell: (info) => {
          if (info.getValue()) {
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Active
              </TextPill>
            )
          } else {
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Inactive
              </TextPill>
            )
          }
        },
      },
    ]

    if (enableUserTypes) {
      cols.push({
        accessorKey: 'user_type',
        header: 'User Type',
        size: 120,
        cell: (info) => {
          const value = (info.getValue() as string) ?? 'Standard'
          return <span>{value}</span>
        },
      })

      if (isCurrentUserAdmin) {
        cols.push({
          id: 'actions',
          header: 'Actions',
          size: 70,
          meta: {
            isEditable: false,
            headerDisableOrdering: true,
            rightAlign: true,
            isLastColumn: true,
          },
          cell: ({ row }) => {
            const targetUser = row.original
            const isSelf = targetUser.id === user?.id
            return (
              <ActionsDropdown
                className="w-full flex justify-end items-center"
                actions={[
                  {
                    ...createEditAction(
                      () => handleEdit(targetUser),
                      'Edit User Type'
                    ),
                    disabled: isSelf,
                    disabledReason: isSelf
                      ? 'You cannot change your own user type'
                      : undefined,
                  },
                ]}
              />
            )
          },
        })
      }
    }

    return cols
  }, [enableUserTypes, isCurrentUserAdmin, user?.id, handleEdit])

  if (isLoading || !data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  All ${user?.company.name} users who have created an account on First Bite. To add a team member, make sure to invite them using their work email.
  `
  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <S.SettingsTableHeader>
            <OverflownTextTooltip
              tooltipText={descriptionText}
              maxLines={4}
              placement="bottom"
            >
              <S.TableDescriptionText>{descriptionText}</S.TableDescriptionText>
            </OverflownTextTooltip>
            <InviteUserModal existingUsers={data} />
          </S.SettingsTableHeader>
        }
        height={'100%'}
        data={data}
        columns={columns}
      />

      <FbModal
        open={!!selectedUser}
        onOpenChange={(open) => {
          if (!open) setSelectedUser(null)
        }}
        title="Edit User Type"
        description={
          selectedUser
            ? `Change user type for ${selectedUser.first_name} ${selectedUser.last_name}`
            : ''
        }
        loading={isSubmitting}
        blockAccept={!selectedType || selectedType === selectedUser?.user_type}
        onAccept={handleSubmit}
        acceptButtonText="Update"
        size="md"
      >
        <Select
          control={control}
          name="user_type"
          label="User Type"
          options={userTypes ?? []}
          isClearable={false}
          disabled={isSubmitting}
        />
      </FbModal>
    </DataTableContainer>
  )
}
